/**
 * SecurityScoreCard Component
 *
 * Display security score with grade and breakdown
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Shield, AlertTriangle, Package, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SecurityScoreCardProps {
  score: number; // 0-100
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  breakdown: {
    vulnerabilities: number;
    outdatedDeps: number;
    directDeps: number;
    devDeps: number;
  };
  lastScan?: Date;
}

/**
 * Grade color configurations
 */
const gradeConfig: Record<
  'A' | 'B' | 'C' | 'D' | 'F',
  { color: string; bg: string; text: string }
> = {
  A: {
    color: 'text-green-600',
    bg: 'bg-green-100',
    text: 'text-green-800',
  },
  B: {
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
  },
  C: {
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
  },
  D: {
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
  },
  F: {
    color: 'text-red-600',
    bg: 'bg-red-100',
    text: 'text-red-800',
  },
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Circular progress indicator
 */
function CircularProgress({
  score,
  size = 120,
  strokeWidth = 8,
  className,
}: {
  score: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  return (
    <svg width={size} height={size} className={className}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted"
        opacity={0.2}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className={cn('transition-all duration-1000', className)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/**
 * SecurityScoreCard - Display security score with grade
 */
export function SecurityScoreCard({
  score,
  grade,
  breakdown,
  lastScan,
}: SecurityScoreCardProps): React.JSX.Element {
  const config = gradeConfig[grade];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Score
          </CardTitle>
          {lastScan && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3" />
              {formatDate(lastScan)}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Circular Score Display */}
        <div className="flex items-center justify-center relative">
          <CircularProgress
            score={score}
            size={140}
            strokeWidth={10}
            className={config.color}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-4xl font-bold">{score}</div>
            <div
              className={cn(
                'text-2xl font-bold mt-1',
                config.color
              )}
            >
              {grade}
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground">
            Security Breakdown
          </h4>

          {/* Vulnerabilities */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <span>Vulnerabilities</span>
              </div>
              <Badge
                variant={breakdown.vulnerabilities > 0 ? 'destructive' : 'secondary'}
                className="font-mono"
              >
                {breakdown.vulnerabilities}
              </Badge>
            </div>
            <Progress
              value={
                breakdown.vulnerabilities > 0
                  ? Math.min((breakdown.vulnerabilities / 10) * 100, 100)
                  : 0
              }
              className="h-1.5"
            />
          </div>

          {/* Outdated Dependencies */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-yellow-600" />
                <span>Outdated Deps</span>
              </div>
              <Badge
                variant={breakdown.outdatedDeps > 5 ? 'destructive' : 'secondary'}
                className="font-mono"
              >
                {breakdown.outdatedDeps}
              </Badge>
            </div>
            <Progress
              value={
                breakdown.outdatedDeps > 0
                  ? Math.min((breakdown.outdatedDeps / 20) * 100, 100)
                  : 0
              }
              className="h-1.5"
            />
          </div>

          {/* Dependencies Summary */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Direct Dependencies</span>
              <span className="font-mono text-xs">{breakdown.directDeps}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Dev Dependencies</span>
              <span className="font-mono text-xs">{breakdown.devDeps}</span>
            </div>
          </div>
        </div>

        {/* Grade Badge */}
        <div
          className={cn(
            'flex items-center justify-center gap-2 p-3 rounded-lg',
            config.bg
          )}
        >
          <span className={cn('text-sm font-semibold', config.text)}>
            Security Grade: {grade}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
